import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

type Args<TName extends string, TArrayName extends string> = {
  names?: TName[];
  arrayNames?: TArrayName[];
};

export type MultipleQueryParamStateAction<
  TName extends string,
  TArrayName extends string,
> = (
  values: Partial<
    Record<TName, string | null | undefined> &
      Record<TArrayName, string[] | null | undefined>
  >,
  options?: { replace?: boolean; preventScrollReset?: boolean },
) => void;

export type MultipleQueryParamState<
  TName extends string,
  TArrayName extends string,
> = [
  Partial<Record<TName, string>> & Record<TArrayName, string[]>,
  MultipleQueryParamStateAction<TName, TArrayName>,
];

export function useMultipleQueryParamStates<
  TName extends string = never,
  TArrayName extends string = never,
>({
  names,
  arrayNames,
}: Args<TName, TArrayName>): MultipleQueryParamState<TName, TArrayName> {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = names?.reduce(
    (acc, name) => Object.assign(acc, { [name]: searchParams.get(name) }),
    {},
  );

  const arrayValues = arrayNames?.reduce(
    (acc, arrayName) =>
      Object.assign(acc, { [arrayName]: searchParams.getAll(arrayName) }),
    {} as Record<TArrayName, string[]>,
  );

  const setValues = useCallback(
    (
      newValues: Partial<
        Record<TName, string | null | undefined> &
          Record<TArrayName, string[] | null | undefined>
      >,
      options?: { replace?: boolean; preventScrollReset?: boolean },
    ) => {
      setSearchParams(
        (searchParamsHandler) => {
          if (names) {
            for (const name of names) {
              if (!(name in newValues)) continue;

              const value = newValues[name as keyof typeof newValues];
              if (value === null || typeof value === 'undefined') {
                searchParamsHandler.delete(name);
              } else {
                searchParamsHandler.set(name, String(value));
              }
            }
          }

          if (arrayNames) {
            for (const arrayName of arrayNames) {
              if (!(arrayName in newValues)) continue;

              searchParamsHandler.delete(arrayName);

              const value = newValues[arrayName as keyof typeof newValues];
              if (Array.isArray(value)) {
                for (const arrayItem of value) {
                  searchParamsHandler.append(arrayName, String(arrayItem));
                }
              }
            }
          }

          return searchParamsHandler;
        },
        {
          replace: options?.replace,
          preventScrollReset: options?.preventScrollReset,
        },
      );
    },
    [setSearchParams, names, arrayNames],
  );

  return [
    { ...values, ...arrayValues } as Partial<
      Record<TName, string | undefined>
    > &
      Record<TArrayName, string[]>,
    setValues,
  ];
}
